import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MENU_DIRECTION, PAGINATION_SIZES } from 'components/constants';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import Pagination from 'components/Pagination/Pagination';
import { WebhookDeliveriesTable } from 'components/WebhookDeliveries/WebhookDeliveriesTable';
import { getCustomerWebhookDeliveriesRequest, getCustomerWebhookDeliveryRequest } from 'services/requestAgent';
import { convertDate } from 'services/utils';
import Button from 'uikit/Button/Button';
import { CustomDatePicker } from 'uikit/CustomDatePicker';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';

const DELIVERY_STATUSES = ['PENDING', 'RETRYING', 'DELIVERED', 'FAILED'];

const EMPTY_FILTERS = {
  webhook_id: '',
  statuses: [],
  from_date: null,
  to_date: null,
  transaction_number: ''
};

// Paginated delivery history of all customer webhooks (incl. test sends), details loaded on expand
export const WebhookDeliveryHistory = ({ customerNumber, webhookList, reloadToken }) => {
  const i18n = useContext(i18nContext);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGINATION_SIZES[0]);
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const loadDeliveries = useCallback(async () => {
    if (!customerNumber) return;
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await getCustomerWebhookDeliveriesRequest(customerNumber, page, size, {
        ...appliedFilters,
        from_date: convertDate(appliedFilters.from_date),
        to_date: convertDate(appliedFilters.to_date)
      });
      setPageData(response);
    } catch (err) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [customerNumber, page, size, appliedFilters]);

  // reloadToken changes after a test send, which is recorded in the history
  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries, reloadToken]);

  const loadDetails = (deliveryId) => getCustomerWebhookDeliveryRequest(customerNumber, deliveryId);

  const handleSetFilter = (name, value) => setFilters((prev) => ({ ...prev, [name]: value }));

  const handleApply = () => {
    setPage(0);
    setAppliedFilters(filters);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const handleSetPageSize = (newSize) => {
    setPage(0);
    setSize(newSize);
  };

  const webhookOptions = [
    { key: '', value: i18n.getMessage('webhookTab.history.filter.allWebhooks') },
    ...webhookList.map((webhook) => ({ key: webhook.id, value: webhook.webhookUrl }))
  ];
  const statusOptions = DELIVERY_STATUSES.map((status) => ({
    key: status,
    value: i18n.getMessage(`webhookDeliveries.status.${status}`)
  }));

  const renderPagination = () => (
    <Pagination
      isLoading={isLoading}
      isShowPaginationSize={true}
      paginationSize={size}
      handleChangePaginationSize={handleSetPageSize}
      currentPage={page}
      numberOfPages={pageData?.total_pages || 0}
      setCurrentPage={setPage}
      menuDirection={MENU_DIRECTION.DOWN}
    />
  );

  return (
    <div className={'webhook-list-section webhook-history-section'}>
      <div className={'webhook-list-header'}>
        <h3 className={'webhook-configuration-title webhook-list-title'}>
          {i18n.getMessage('webhookTab.history.title')}
        </h3>
        <Button
          className={'webhook-actions-button other-buttons'}
          roleType={'button'}
          size={'small'}
          type={'outline'}
          onClick={loadDeliveries}
          isDisabled={isLoading}
        >
          {i18n.getMessage('webhookTab.history.button.refresh')}
        </Button>
      </div>

      <div className={'webhook-history-filters'}>
        <InputDropDown
          className={'webhook-history-filter webhook-history-filter-webhook'}
          label={i18n.getMessage('webhookTab.history.filter.webhook')}
          name={'webhook_id'}
          value={filters.webhook_id}
          options={webhookOptions}
          onChange={handleSetFilter}
          isDisabled={isLoading}
        />
        <InputDropDown
          className={'webhook-history-filter'}
          label={i18n.getMessage('webhookTab.history.filter.status')}
          name={'statuses'}
          value={filters.statuses}
          options={statusOptions}
          onChange={handleSetFilter}
          isMulti
          isDisabled={isLoading}
        />
        <div className={'webhook-history-date'}>
          <span>{i18n.getMessage('webhookTab.history.filter.from')}</span>
          <CustomDatePicker
            onChange={handleSetFilter}
            name={'from_date'}
            value={filters.from_date}
            maxDate={filters.to_date || new Date()}
          />
        </div>
        <div className={'webhook-history-date'}>
          <span>{i18n.getMessage('webhookTab.history.filter.to')}</span>
          <CustomDatePicker
            onChange={handleSetFilter}
            name={'to_date'}
            value={filters.to_date}
            maxDate={new Date()}
            minDate={filters.from_date}
          />
        </div>
        <Input
          className={'webhook-history-filter'}
          label={i18n.getMessage('webhookTab.history.filter.transactionNumber')}
          name={'transaction_number'}
          value={filters.transaction_number}
          onChange={(e) => handleSetFilter('transaction_number', e.target.value)}
          isDisabled={isLoading}
        />
        <div className={'webhook-actions-buttons-wrapper'}>
          <Button
            className={'webhook-actions-button other-buttons'}
            roleType={'button'}
            size={'small'}
            type={'primary'}
            onClick={handleApply}
            isDisabled={isLoading}
          >
            {i18n.getMessage('webhookTab.history.filter.apply')}
          </Button>
          <Button
            className={'webhook-actions-button other-buttons'}
            roleType={'button'}
            size={'small'}
            type={'outline'}
            onClick={handleReset}
            isDisabled={isLoading}
          >
            {i18n.getMessage('webhookTab.history.filter.reset')}
          </Button>
        </div>
      </div>

      {isError && <p className={'webhook-history-error'}>{i18n.getMessage('webhookDeliveries.loadError')}</p>}

      {!pageData && isLoading ? (
        <Loader className={'webhook-history-loader'} />
      ) : (
        !isError && (
          <>
            {renderPagination()}
            <WebhookDeliveriesTable
              key={`${page}-${size}-${JSON.stringify(appliedFilters)}`}
              deliveries={pageData?.content || []}
              isShowTransaction
              loadDetails={loadDetails}
            />
            {renderPagination()}
          </>
        )
      )}
    </div>
  );
};

WebhookDeliveryHistory.propTypes = {
  customerNumber: PropTypes.string,
  webhookList: PropTypes.array,
  reloadToken: PropTypes.number
};
